import {
  MassActionsContextProvider,
  useMassActionsContext,
} from '@components/MassActions/contexts/MassActions.context';
import ListScreen from '@components/v2/layout/ListScreen/ListScreen';
import { ListScreenFiltersConfig } from '@components/v2/layout/ListScreen/listScreen.types';
import Breadcrumb from '@components/v2/ui/Breadcrumb/Breadcrumb';
import Modal from '@components/v2/ui/Modal/Modal';
import { arrayMove } from '@dnd-kit/sortable';
import { LinearProgress } from '@mui/material';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { InfinityListManagerWithoutCustomParams } from '../../../../components/Hooks/infinity-list/infinityList.types';
import useListActions from '../../../../components/Hooks/useListActions';
import { useListMassActions } from '../../../../components/MassActions/hooks/useListMassActions';
import useHashFlag from '../../../../hooks/useHashFlag';
import { useInfiniteListWithCheckbox } from '../../../../hooks/useInfiniteListWithCheckbox/useInfiniteListWithCheckbox';
import { UseInfiniteListWithCheckboxTransformers } from '../../../../hooks/useInfiniteListWithCheckbox/utils/useInfiniteListWithCheckbox.transformers';
import useSearchParams from '../../../../hooks/useSearchParams';
import LangEnum from '../../../../lang/LangEnum';
import HASH_FLAGS from '../../../../routes/hashFlags.const';
import { RoutePageProps } from '../../../../routes/routes.types';
import { CatalogListFilters } from '../../../../services/modules/catalogs/catalogsBulk.repository.types';
import snackbarStore from '../../../../stores/Snackbar.store';
import { INFINITE_SCROLL_ROOT_MARGIN } from '../../../../utils/constants';
import { ShareCatalogTestIds } from '../../../../utils/testIds/ShareCatalog.testIds';
import { CatalogTestIds } from '../../../../utils/testIds/catalog.testIds';
import { CLASSIFICATIONS_PREFIX } from '../../../classifications/classification-filter.utils';
import { UsedInOfflineMode } from '../../../offline/types/UsedInOfflineMode';
import { RACHEL_LIST_SCOPE, RachelResource } from '../../../rachel/effects/uiEffect.schema';
import { useRachelEventListener } from '../../../rachel/effects/useRachelEventListener';
import ShareCatalogModal from '../../../shared-entities/ShareCatalogModal';
import { useTranslation } from '../../../translation/TranslationContext';
import { Catalog } from '../../catalog.types';
import CatalogExportModal from '../../components/CatalogExportModal/CatalogExportModal';
import CatalogBulkModals from '../../components/CatalogListMassActions/CatalogBulkModals';
import {
  CATALOG_LIST_FILTERS_KEYS,
  formatCatalogListFilters,
} from '../../filter/catalogListFilter.utils';
import useCanManageBrandCatalogs from '../../hooks/useCanManageBrandCatalogs';
import useCatalogListMassActions from '../../hooks/useCatalogListMassActions';
import { buildCatalogMassActions } from '../../hooks/utils/catalogListMassActions.utils';
import { catalogsRepository } from '../../repositories/catalogs.repository';
import { ConfirmModalState } from './catalogListPage.types';
import CatalogListEmptyState from './components/CatalogListEmptyState';
import CatalogListGrid from './components/CatalogListGrid';
import CatalogListPageFilters from './components/CatalogListPageFilters';
import CatalogListPageOfflineFilters from './components/CatalogListPageOfflineFilters';
import CatalogListTabs from './components/CatalogListTabs';
import { useCatalogListDragAndDrop } from './hooks/useCatalogListDragAndDrop';
import { ReorderCatalogListParams } from './utils/catalogListDragAndDrop.types';
import {
  catalogListFetcherWithStatusCounts,
  offlineCatalogListFetcher,
} from './utils/catalogListFetcherWithStatusCounts.utils';
import { CatalogListTabType } from './utils/catalogListTabs/catalogListTabType.enum';
import {
  CatalogListTab,
  CatalogListTabCounts,
} from './utils/catalogListTabs/catalogListTabs.types';
import {
  CATALOG_LIST_STATUS_SEARCH_PARAM,
  getCatalogListTabCounts,
  getCatalogListTabSearchValue,
  getCatalogStatusFilterByTab,
  getVisibleCatalogListTabs,
  resolveCatalogListTab,
} from './utils/catalogListTabs/catalogListTabs.utils';

interface CatalogListTabsState {
  visibleTabs: CatalogListTab[];
  selectedTab: CatalogListTabType;
  tabCounts: CatalogListTabCounts;
  onChangeTab: (tab: CatalogListTab) => void;
}

const REORDER_BLOCKING_FILTER_KEYS = new Set<string>(
  Object.values(CATALOG_LIST_FILTERS_KEYS).filter(
    (key) => key !== CATALOG_LIST_FILTERS_KEYS.status,
  ),
);

type CatalogListPageContentProps = RoutePageProps &
  UsedInOfflineMode & {
    list: InfinityListManagerWithoutCustomParams<Catalog>;
    tabs: CatalogListTabsState;
    filters: ListScreenFiltersConfig;
  };

const CatalogListPageContent = ({
  list,
  tabs,
  filters,
  isOfflineMode = false,
}: CatalogListPageContentProps) => {
  const { handleMessage } = snackbarStore;
  const { translate } = useTranslation();
  const { searchParams } = useSearchParams(undefined);
  const [item, setItem] = useState<Catalog>();
  const { open: openCreationModal } = useHashFlag(HASH_FLAGS.createCatalog);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    open: false,
    item: undefined,
  });

  useRachelEventListener({
    resource: RachelResource.CATALOG,
    scopeId: RACHEL_LIST_SCOPE,
    onChange: () => list.reload(),
  });

  const canEditCatalogs = useCanManageBrandCatalogs();
  const readMode = isOfflineMode || !canEditCatalogs;

  const { massActionFilter } = useMassActionsContext();
  const selectedItems = new Set(list.state.selecteds);

  const massActions = useCatalogListMassActions({
    listItems: list.state.items,
    totalItems: list.state.totalItems,
    reload: list.reload,
    activeFilters: formatCatalogListFilters(searchParams) as CatalogListFilters,
    isMassActionFilter: massActionFilter,
    selectedItems,
  });
  useListMassActions({
    list,
    actions: buildCatalogMassActions({
      isActivateHidden: massActions.isActivateHidden,
      isInactivateHidden: massActions.isInactivateHidden,
      isRemoveHidden: massActions.isRemoveHidden,
    }),
    onClickAction: massActions.handleMassAction,
    testId: CatalogTestIds.CATALOG_MASS_ACTIONS_BAR,
  });

  const isAnySelected = massActions.totalSelected > 0;

  const hasActiveFilters = useMemo(() => {
    return Object.entries(searchParams).some(([key, value]) => {
      const isFilterKey =
        REORDER_BLOCKING_FILTER_KEYS.has(key) || key.startsWith(CLASSIFICATIONS_PREFIX);
      if (!isFilterKey) {
        return false;
      }
      return (
        value !== undefined && value !== '' && (Array.isArray(value) ? value.length > 0 : true)
      );
    });
  }, [searchParams]);

  const isFiltered = hasActiveFilters || tabs.selectedTab !== CatalogListTabType.ALL;

  const listActions = useListActions<Catalog, string>({
    apiRoute: '/catalogs',
    screenRoute: '/catalogs',
    replaceItems: () => {
      // noop
    },
    changeSelecteds: () => {
      // noop
    },
    onSearch: () => {
      // noop
    },
    reload: list.reload,
  });

  const hasNextPage = list.state.hasMore;

  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: list.state.isLoadingPage || list.state.isLoadingMore,
    hasNextPage,
    onLoadMore: list.loadMoreRows,
    rootMargin: INFINITE_SCROLL_ROOT_MARGIN,
  });

  const handleReorder = async ({ catalogId, position }: ReorderCatalogListParams) => {
    const items = list.state.items;
    const oldIndex = items.findIndex((item) => item.id === catalogId);

    if (oldIndex === -1) {
      return;
    }

    list.replaceAllItems(arrayMove(items, oldIndex, position));

    const response = await catalogsRepository(catalogId).update({ position }, {});

    if (!response.ok) {
      list.reload();
      handleMessage({ type: 'error', message: translate(LangEnum.CATALOGS_REORDER_ERROR) });
    }
  };

  const dragAndDrop = useCatalogListDragAndDrop({
    isEnabled: !readMode && !isFiltered,
    itemIds: list.state.items.map((item) => item.id),
    onReorder: handleReorder,
  });

  const handleCatalogUpdated = (updatedCatalog: Catalog) => {
    const currentCatalog = list.state.items.find((item) => item.id === updatedCatalog.id);

    if (currentCatalog?.active !== updatedCatalog.active) {
      list.reload();
      return;
    }

    list.replaceAllItems(
      list.state.items.map((item) => (item.id === updatedCatalog.id ? updatedCatalog : item)),
    );
  };

  const handleCloseConfirmModal = () => {
    setConfirmModal({ open: false, item: undefined });
  };

  const handleRemove = async (id: string) => {
    await listActions.handleRemove(id);
    handleCloseConfirmModal();
  };

  return (
    <>
      <ListScreen
        filters={filters}
        scrollRef={rootRef}
        toolbar={{
          tabs: (
            <CatalogListTabs
              visibleTabs={tabs.visibleTabs}
              selectedTab={tabs.selectedTab}
              tabCounts={tabs.tabCounts}
              totalCount={list.state.totalItems}
              isLoading={list.state.isLoadingPage}
              onChangeTab={tabs.onChangeTab}
            />
          ),
        }}
        header={{
          breadcrumb: <Breadcrumb items={[{ label: LangEnum.CATALOGS_TITLE }]} />,
          primaryAction: {
            label: LangEnum.GLOBAL_CREATE_NEW_CATALOG,
            icon: Plus,
            variant: 'text',
            onClick: openCreationModal,
            isHidden: readMode,
            testId: CatalogTestIds.CATALOG_CREATE_BUTTON,
          },
        }}
      >
        {() => (
          <>
            {list.state.isLoadingPage && <LinearProgress />}
            <CatalogListGrid
              items={list.state.items}
              readMode={readMode}
              isOfflineMode={isOfflineMode}
              loadMoreSentinel={hasNextPage && <div ref={infiniteRef} />}
              dragAndDrop={dragAndDrop}
              setItem={setItem}
              setConfirmModal={setConfirmModal}
              onCatalogUpdated={handleCatalogUpdated}
              selection={{
                isSelectedAll: massActionFilter,
                selectedIds: selectedItems,
                isAnySelected,
                onToggleSelect: list.checkbox.toggleSelect,
              }}
            />
            {!list.state.isLoadingPage && list.state.items.length === 0 && (
              <CatalogListEmptyState
                isFiltered={isFiltered}
                canCreate={!readMode}
                onCreate={openCreationModal}
              />
            )}
          </>
        )}
      </ListScreen>

      {item?.id && (
        <ShareCatalogModal
          isOpen={Boolean(item)}
          catalogId={item.id}
          catalogName={item.name}
          enablePublicSharing={!!item.enablePublicSharing && !item.isDefault}
          testId={ShareCatalogTestIds.DIALOG}
          onClose={() => setItem(undefined)}
        />
      )}

      <Modal
        isOpen={confirmModal.open}
        size="standard"
        header={{ title: LangEnum.CATALOGS_CONFIRM_REMOVE_MODAL_TITLE }}
        onClose={handleCloseConfirmModal}
        footer={{
          primaryAction: {
            label: LangEnum.GLOBAL_REMOVE_LABEL,
            onClick: () => confirmModal.item && handleRemove(confirmModal.item.id),
          },
          secondaryAction: {
            label: LangEnum.GLOBAL_CANCEL_LABEL,
            onClick: handleCloseConfirmModal,
          },
        }}
      >
        {translate(LangEnum.CATALOGS_CONFIRM_REMOVE_MODAL_MESSAGE)}
      </Modal>
      <CatalogExportModal />

      <CatalogBulkModals massActions={massActions} />
    </>
  );
};

const CatalogListPage = ({
  isOfflineMode = false,
  ...props
}: RoutePageProps & UsedInOfflineMode) => {
  const canManageBrandCatalogs = useCanManageBrandCatalogs();
  const { searchParams, pushSearchParams } = useSearchParams(undefined);

  const visibleTabs = getVisibleCatalogListTabs(isOfflineMode ? false : canManageBrandCatalogs);
  const selectedTab = resolveCatalogListTab({
    status: searchParams[CATALOG_LIST_STATUS_SEARCH_PARAM],
    visibleTabs,
  });

  const offlineQueryFnParams = {
    [CATALOG_LIST_FILTERS_KEYS.query]: searchParams[CATALOG_LIST_FILTERS_KEYS.query],
  };

  const queryFnParams = useMemo(
    () => ({
      ...formatCatalogListFilters(searchParams),
      status: getCatalogStatusFilterByTab(selectedTab),
      ...(canManageBrandCatalogs ? {} : { active: true }),
    }),
    [searchParams, selectedTab, canManageBrandCatalogs],
  );

  const infiniteListWithCheckbox = useInfiniteListWithCheckbox({
    limit: 20,
    cacheKey: isOfflineMode ? 'offline-catalogs' : 'catalogs',
    defaultOrderBy: { position: 'ASC' },
    queryFn: isOfflineMode ? offlineCatalogListFetcher : catalogListFetcherWithStatusCounts,
    queryFnParams: isOfflineMode ? offlineQueryFnParams : queryFnParams,
  });

  const infinityListManager =
    UseInfiniteListWithCheckboxTransformers.toInfinityListManager(infiniteListWithCheckbox);
  const massActionData =
    UseInfiniteListWithCheckboxTransformers.toMassActionData(infiniteListWithCheckbox);

  const handleChangeTab = (tab: CatalogListTab) => {
    pushSearchParams({
      [CATALOG_LIST_STATUS_SEARCH_PARAM]: getCatalogListTabSearchValue(tab.name),
    });
    infinityListManager.checkbox.toggleSelectBatch([]);
  };

  const renderPage = (filters: ListScreenFiltersConfig) => (
    <CatalogListPageContent
      {...props}
      isOfflineMode={isOfflineMode}
      list={infinityListManager}
      filters={filters}
      tabs={{
        visibleTabs,
        selectedTab,
        tabCounts: getCatalogListTabCounts(infiniteListWithCheckbox.query.countData?.facets),
        onChangeTab: handleChangeTab,
      }}
    />
  );

  const FiltersProvider = isOfflineMode ? CatalogListPageOfflineFilters : CatalogListPageFilters;

  return (
    <MassActionsContextProvider data={massActionData}>
      <FiltersProvider>{renderPage}</FiltersProvider>
    </MassActionsContextProvider>
  );
};

export default CatalogListPage;
